/**
 * The verification queue.
 *
 * Everything the pipeline drafts is stamped "needs review", and this is where
 * that stamp gets discharged. Until it existed the flag meant nothing: 1,785
 * AI comparisons were waiting for a human with nowhere for that human to go.
 *
 * The design question is what a reviewer needs in order to be honest. Not the
 * verdict on its own — anyone will click accept on a plausible sentence — but
 * the clause it is about, the clause it was matched to, and the difference the
 * model claims between them, all readable without leaving the row. A queue
 * that makes accepting easier than checking will be accepted straight through,
 * and the sign-off it produces is worth less than no sign-off at all, because
 * it looks like diligence.
 *
 * So: three buttons, and the middle one is the interesting one. Correcting
 * costs a sentence and produces an eval example; accepting costs a click and
 * produces a checked row. The asymmetry is deliberate and it is the point —
 * corrections are how the pipeline gets better, so they must be easy enough to
 * be worth doing rather than easier to skip.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type QueueItem } from '../lib/api.js';
import { Sample } from './Sample.js';

const VERDICT_LABELS: Record<string, string> = {
  primary_better: 'Our agreement is better',
  comparable: 'Materially the same',
  comparator_better: 'The comparator is better',
  no_primary_equivalent: 'We have nothing on this',
  no_comparator_equivalent: 'They have nothing on this',
};

/** What a past reviewer did, said in the past tense so it cannot read as now. */
const PRIOR_VERDICT_LABELS: Record<string, string> = {
  accepted: 'A rep accepted it',
  corrected: 'A rep corrected it',
  rejected: 'A rep rejected it',
};

/** What "correct it" means, which depends on what is being corrected. */
const CORRECT_LABEL: Record<string, string> = {
  alignment: 'Change the verdict',
  clause: 'Rewrite it',
  clause_leverage: 'Rewrite the ask',
  entitlement: 'Fix the figure',
};

/** The verdicts a reviewer can substitute, in the order they are argued. */
const VERDICT_OPTIONS = [
  'comparator_better',
  'primary_better',
  'comparable',
  'no_comparator_equivalent',
] as const;

/**
 * The correction payload, shaped for the row being corrected.
 *
 * /verify/review reads a different object per entity table: `textPlain` for a
 * clause, the four leverage fields for clause_leverage, and verdict/deltaNote
 * for an alignment. Sending the alignment shape for all three meant a rep's
 * corrected sentence was read by nothing and the model's draft was stamped
 * verified in their name.
 *
 * A leverage correction goes to `ask` because that is the field a rep is
 * overwhelmingly correcting — the demand itself — and the note carries the
 * rest. Better one field a person can actually fix than four they must fill.
 */
function correctionFor(
  item: QueueItem,
  verdict: string,
  note: string,
): Record<string, unknown> {
  if (item.entityTable === 'clause') return { textPlain: note };
  if (item.entityTable === 'clause_leverage') return { ask: note };
  return { verdict, deltaNote: note || null };
}

export function Verify({ onOpenClause }: { onOpenClause: (number: string) => void }) {
  const client = useQueryClient();
  const [sampleOnly, setSampleOnly] = useState(false);
  const queue = useQuery({
    queryKey: ['verifyQueue', sampleOnly],
    queryFn: () => api.verifyQueue(sampleOnly),
  });
  const evals = useQuery({ queryKey: ['verify-evals'], queryFn: api.verifyEvals });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['verify-queue'] });
    void client.invalidateQueries({ queryKey: ['verify-evals'] });
  };

  const items = queue.data?.items ?? [];
  const total = queue.data?.total ?? 0;
  const tally = evals.data?.tally ?? {};
  const reviewed = (tally['accepted'] ?? 0) + (tally['corrected'] ?? 0) + (tally['rejected'] ?? 0);

  return (
    /*
     * `.screen` for the padding, and `measure-wide` rather than a reading
     * column: two clauses side by side is the whole job here, and a 68ch cap
     * would put them at 34ch each — narrower than either provision's own
     * indent, which is what turns a comparison into two paragraphs of scroll.
     */
    <div className="screen">
      <div className="measure-wide mx-auto">
        <Sample sampleOnly={sampleOnly} onSampleOnly={setSampleOnly} />
        <header>
          {/* Sans, not serif. §4 reserves the serif for the agreement's own
              words; a screen title is interface, and setting it in the reading
              register made the page heading look like a quoted clause. */}
          <h1 className="m-0 text-[25px] font-semibold leading-[1.15] tracking-tight">
            Needs checking
          </h1>
          <p className="measure mt-2 text-[13.5px] leading-relaxed text-[var(--color-muted)]">
            Every comparison here was drafted by a model against the real text of both instruments.
            None of it is a finding until someone has read the two clauses and said so. Nothing on
            this screen is visible to the wider staff, and nothing leaves it until it is checked.
          </p>

          <p className="label mt-3">
            {total} waiting
            {items.length < total ? ` · showing the ${items.length} most consequential` : ''}
            {reviewed > 0 ? ` · ${reviewed} checked` : ''}
            {tally['corrected'] ? ` · ${tally['corrected']} corrected` : ''}
          </p>
        </header>

        {queue.isPending ? <p className="label mt-6">Loading…</p> : null}

        {!queue.isPending && items.length === 0 ? (
          <p className="measure mt-6 text-[13.5px] leading-relaxed text-[var(--color-muted)]">
            Nothing is waiting. When the comparison pipeline next runs, what it drafts will appear
            here.
          </p>
        ) : null}

        {/* A gap between cards, not a rule: six of three hundred and sixty get
            checked, and the reviewer's first act on each is to find where one
            item ends. */}
        <ul className="mt-5 flex flex-col gap-3">
          {items.map((item) => (
            <QueueRow
              key={`${item.entityTable}-${item.entityId}`}
              item={item}
              onOpenClause={onOpenClause}
              onReviewed={refresh}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

function QueueRow({
  item,
  onOpenClause,
  onReviewed,
}: {
  item: QueueItem;
  onOpenClause: (number: string) => void;
  onReviewed: () => void;
}) {
  const [correcting, setCorrecting] = useState(false);
  const [verdict, setVerdict] = useState<string>(
    item.fields.find((f) => f.label === 'Verdict')?.value ?? 'comparable',
  );
  const [note, setNote] = useState('');
  /**
   * Whether the reviewer has actually moved the verdict.
   *
   * On an alignment, changing it IS the correction — "these two are materially
   * the same, not one better than the other" is precisely the ground truth the
   * verdict-accuracy eval set is made of, and it needs no prose to be useful.
   * Demanding a sentence on top of it made the honest answer more work than
   * accepting a wrong one, and a suppressed correction becomes an accepted
   * wrong verdict, which is strictly worse than a correction with no reasons.
   *
   * The sentence stays mandatory where it is the whole correction: a
   * plain-language rewrite or a leverage block, where nothing else changes.
   */
  const draftedVerdict = item.fields.find((f) => f.label === 'Verdict')?.value ?? null;
  const verdictMoved = item.entityTable === 'alignment' && verdict !== draftedVerdict;
  const [error, setError] = useState<string | null>(null);

  const review = useMutation({
    mutationFn: (body: Parameters<typeof api.review>[0]) => api.review(body),
    onSuccess: onReviewed,
    onError: (e: Error) => setError(e.message),
  });

  const field = (label: string): string | null =>
    item.fields.find((f) => f.label === label)?.value ?? null;
  const drafted = field('Verdict');
  /*
   * The comparator field is labelled with the instrument and clause together —
   * "Award 2020 clause 22.5" — so a lookup for 'Matched clause' found nothing
   * and every one of the 360 matched pairs reported "no clause matched".
   */
  const matched =
    item.fields.find((f) => /\bclause\b/i.test(f.label) && !f.label.startsWith('Our clause'))
      ?.label ?? null;

  return (
    <li className="card">
      {/* The clause, the heading and which model drafted this — the three
          things that decide whether a reviewer opens the row at all — on the
          card's own header rule, where every other surface puts them. */}
      <div className="card-header">
        <div className="flex min-w-0 flex-wrap items-baseline gap-2">
          {item.clauseNumber ? (
            <button
              onClick={() => onOpenClause(item.clauseNumber!)}
              className="hit clause-number text-[12px] underline decoration-[var(--color-rule-strong)] underline-offset-2"
              title={item.clauseHeading ?? item.clauseNumber}
            >
              {item.clauseNumber}
            </button>
          ) : null}
          <span className="text-[13.5px] font-semibold leading-snug tracking-tight">
            {item.clauseHeading ?? item.title}
          </span>
        </div>
        {/* The provenance stamp the rest of the app uses, rather than a bare
            mono label: the model name is the claim that nothing here has been
            read by a person, which is the whole subject of the screen. */}
        <span className="stamp stamp-ai shrink-0">{item.model ?? 'unknown model'}</span>
      </div>

      <p className="text-[13px]">
        <strong style={{ fontWeight: 600 }}>
          {drafted ? (VERDICT_LABELS[drafted] ?? drafted) : 'No verdict'}
        </strong>
        {/* The matched number is stated, not linked. It belongs to a
            comparator instrument and Read mode holds only the agreement, so
            the link this replaces 404'd on every comparator citation. The
            text itself is below, which is what a reviewer needed anyway. */}
        {matched ? (
          <span className="clause-number ml-1 text-[11px]"> · their clause {matched}</span>
        ) : (
          <span className="text-[var(--color-muted)]"> · no clause matched</span>
        )}
      </p>

      {field('Difference') ? (
        <p className="measure register-reading mt-1.5 border-l-2 border-[var(--color-ink)] pl-3 text-[13.5px] leading-relaxed">
          {field('Difference')}
        </p>
      ) : null}

      {/*
        Both provisions, side by side, each fenced by its own rule.

        The two texts a reviewer has to hold against each other were once
        separated by a "Difference" column repeating the sentence already
        quoted above it — a three-column read with the two things being
        compared at opposite ends. The delta note stays where it is, once, and
        the clauses sit next to each other.

        What was left was two unfenced 12px columns four rems apart, so on a
        wide screen the eye had to find where one clause stopped and the other
        started with nothing but whitespace to go on — and 12px serif is under
        the 13.5–14.5px §7 sets for agreement text, on the one screen where
        every word is read against another word. Six of three hundred and sixty
        rows have been checked; the cost of that is measured in how long a
        reviewer keeps going.
      */}
      <div className="section-rule mt-3 grid gap-x-5 gap-y-4 pt-3 md:grid-cols-2">
        {/*
          Every field the queue sent, in the order it sent them, minus the
          difference — which is the blockquote above.
          An earlier filter kept only labels starting with "Our clause" or
          containing "clause ", which was right when alignments were the only
          reviewable thing and silently wrong once they were not: a leverage
          row carries Ask, Precedent, Pressure and Watch, and a plain row
          carries Plain English, and none of those survived. A rep saw the
          verbatim clause, a "Checked — this is right" button, and none of the
          drafted words they were being asked to approve. That is exactly the
          "accepting easier than checking" failure this screen exists to stop.
        */}
        {item.fields
          .filter((f) => f.label !== 'Verdict' && f.label !== 'Difference')
          .map((f) => (
            <div key={f.label} className="border-l border-[var(--color-rule-strong)] pl-3">
              <p className="label mb-1">{f.label}</p>
              <p
                className="register-reading text-[13.5px] leading-[1.55]"
                style={{ color: f.value ? undefined : 'var(--color-muted)' }}
              >
                {f.value ?? 'Nothing was matched, so there is no text to compare.'}
              </p>
            </div>
          ))}
      </div>

      {/* What was decided here before, when there is such a thing.

          Deliberately not styled as a verified badge, and deliberately not
          suppressing the row: the draft above is a different sentence from a
          different model, so the earlier decision is context and not a
          sign-off. It is shown because the alternative is what actually
          happened — six reviews, one of them a rep's own written correction
          about casual engagement, sitting in the table reachable by nothing
          after a re-import rebuilt the rows they pointed at. */}
      {item.priorReviews.length > 0 ? (
        <div
          className="measure mt-3 border-l-2 pl-3"
          style={{ borderColor: 'var(--color-rule-strong)', color: 'var(--color-muted)' }}
        >
          <p className="label">Decided before, on a draft that no longer exists</p>
          {item.priorReviews.map((r) => (
            <p key={r.reviewedAt} className="register-reading mt-1 text-[12.5px] leading-relaxed">
              <strong style={{ fontWeight: 600 }}>{PRIOR_VERDICT_LABELS[r.verdict] ?? r.verdict}</strong>
              {r.correctedVerdict ? (
                <span> · corrected to {VERDICT_LABELS[r.correctedVerdict] ?? r.correctedVerdict}</span>
              ) : null}
              {r.note ? <span> — “{r.note}”</span> : null}
              {r.correctedNote && r.correctedNote !== r.note ? (
                <span className="mt-1 block">{r.correctedNote}</span>
              ) : null}
            </p>
          ))}
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="mt-2 text-[12px]" style={{ color: 'var(--color-defect)' }}>
          {error}
        </p>
      ) : null}

      {/*
        Three buttons, and the first two the same size on purpose.

        All three were 9px uppercase mono underlines — the register this app
        uses for panel labels — so the three decisions a reviewer is here to
        make were set smaller than the word "Verdict" above them, and the
        cheapest of the three to press was the one that signs off a model's
        sentence in a person's name. Accept and correct now carry identical
        weight, which is the asymmetry this screen was built around: a
        correction is worth more than an acceptance and must not be the harder
        thing to find.
      */}
      <div className="section-rule mt-3 flex flex-wrap items-center gap-2.5 pt-2.5">
        <button
          onClick={() =>
            review.mutate({
              entityTable: item.entityTable,
              entityId: item.entityId,
              verdict: 'accepted',
            })
          }
          disabled={review.isPending}
          className="hit rounded-[6px] border border-[var(--color-ink)] px-3 py-1.5 text-[12.5px] disabled:opacity-40"
        >
          Checked — this is right
        </button>
        {/*
          Named for what it does to this row.
          "Correct it" reads as "rewrite the text", so on a comparison — where
          the commonest correction is that the two clauses are actually much
          of a muchness — there was no visible way to say so.
        */}
        <button
          onClick={() => setCorrecting((o) => !o)}
          aria-expanded={correcting}
          className="hit rounded-[6px] border border-[var(--color-ink)] px-3 py-1.5 text-[12.5px]"
        >
          {correcting ? 'Cancel' : CORRECT_LABEL[item.entityTable] ?? 'Correct it'}
        </button>
        <button
          onClick={() =>
            review.mutate({
              entityTable: item.entityTable,
              entityId: item.entityId,
              verdict: 'rejected',
              ...(note.trim() ? { note: note.trim() } : {}),
            })
          }
          disabled={review.isPending}
          className="hit ml-auto text-[12px] text-[var(--color-muted)] underline underline-offset-2 disabled:opacity-40"
        >
          Not a real comparison
        </button>
      </div>

      {correcting ? (
        <div className="measure mt-3 border-l-2 border-[var(--color-ink)] pl-3">
          {/*
            A verdict is an alignment concept. "Our agreement is better" says
            nothing about a paraphrase or a leverage block, and offering it on
            those rows invited a reviewer to pick one and believe they had
            recorded something.
          */}
          {item.entityTable === 'alignment' ? (
            <>
              <label className="label" htmlFor={`v-${item.entityTable}-${item.entityId}`}>
                What it should say
              </label>
              <select
                id={`v-${item.entityTable}-${item.entityId}`}
                value={verdict}
                onChange={(e) => setVerdict(e.target.value)}
                className="hit mt-1 w-full rounded-[5px] border border-[var(--color-rule-strong)] bg-transparent px-2 py-1.5 text-[13px]"
              >
                {VERDICT_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {VERDICT_LABELS[v]}
                  </option>
                ))}
              </select>
            </>
          ) : null}

          <label className="label mt-3 block" htmlFor={`n-${item.entityTable}-${item.entityId}`}>
            {verdictMoved
              ? 'Why, if it is worth saying — optional now the verdict has moved'
              : 'The difference, in one checkable sentence'}
          </label>
          <textarea
            id={`n-${item.entityTable}-${item.entityId}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Name the figures where there are figures — someone will have to defend this across a table."
            className="mt-1 w-full rounded-[5px] border border-[var(--color-rule-strong)] bg-transparent p-2 text-[13px] leading-snug"
          />

          <button
            onClick={() => {
              setError(null);
              review.mutate(
                {
                  entityTable: item.entityTable,
                  entityId: item.entityId,
                  verdict: 'corrected',
                  /*
                   * Shaped for the row being corrected.
                   *
                   * This always sent { verdict, deltaNote }, which the clause
                   * and clause_leverage branches of /verify/review do not read.
                   * A rep correcting a plain rewrite or a leverage block had
                   * their sentence dropped and the model's draft stamped
                   * verified in their name — the worst outcome available here,
                   * because it looks exactly like a checked row.
                   */
                  corrected: correctionFor(item, verdict, note.trim()),
                  ...(note.trim() ? { note: note.trim() } : {}),
                },
                { onSuccess: () => setCorrecting(false) },
              );
            }}
            disabled={review.isPending || (!note.trim() && !verdictMoved)}
            className="hit mt-3 rounded-[6px] px-3 py-1.5 text-[12.5px] disabled:opacity-40"
            style={{ background: 'var(--color-ink)', color: 'var(--color-paper)' }}
          >
            Save the correction
          </button>
          {!note.trim() && !verdictMoved ? (
            <p className="mt-1.5 text-[11.5px] leading-snug text-[var(--color-muted)]">
              A correction needs its sentence — that sentence is what makes it
              worth more than the draft.
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
